// Progress Services
// User progress tracking operations using ServiceBase

import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  increment,
  arrayUnion,
  DocumentReference
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError } from '@/shared/api/errors/ApiError';
import ServiceBase from '@/shared/api/base/ServiceBase';

// --- PROGRESS CACHING ---
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface LessonProgress {
  completed: boolean;
  completedAt: any;
  attempts: number;
  timeSpent?: number;
  [key: string]: any;
}

export interface ModuleProgress {
  completed: boolean;
  completedAt: any;
  [key: string]: any;
}

export interface CourseProgress {
  overallProgress: number;
  completedLessons: number;
  totalLessons: number;
  totalInstructionSeconds?: number;
  lessonProgress: Record<string, LessonProgress>;
  moduleProgress: Record<string, ModuleProgress>;
  lastAccessedAt: any;
  createdAt: any;
  enrolled: boolean;
  [key: string]: any;
}

export interface ProgressData {
  [courseId: string]: CourseProgress | any;
}

export interface UserStats {
  totalCoursesEnrolled: number;
  totalLessonsCompleted: number;
  averageProgress: number;
}

class ProgressService extends ServiceBase {
  constructor() {
    super('ProgressService');
  }

  private _getCacheKey(userId: string, courseId: string): string {
    return `progress_${userId}_${courseId}`;
  }

  private _setCache(userId: string, courseId: string, data: CourseProgress): void {
    const key = this._getCacheKey(userId, courseId);
    this.cache.set(key, JSON.parse(JSON.stringify(data)), CACHE_TTL);
  }

  private _getFromCache(userId: string, courseId: string): CourseProgress | null {
    const key = this._getCacheKey(userId, courseId);
    const cached = this.cache.get<CourseProgress>(key);
    if (cached) {
      return JSON.parse(JSON.stringify(cached));
    }
    return null;
  }

  private _invalidateCache(userId: string, courseId: string): void {
    const key = this._getCacheKey(userId, courseId);
    this.cache.invalidate(key);
  }

  private _getUserProgressRef(userId: string): DocumentReference {
    return doc(db, 'users', userId, 'userProgress', 'progress');
  }

  /**
   * Initialize progress for a course on first access
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {number} totalLessons Total lessons in course
   * @returns {Promise<Object>} Initialized progress
   */
  async initializeProgress(userId: string, courseId: string, totalLessons = 0): Promise<CourseProgress> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED Progress for E2E testing');
        return (window as any).MOCK_PROGRESS[courseId] || {
          overallProgress: 0,
          completedLessons: 0,
          totalLessons: totalLessons,
          lessonProgress: {},
          moduleProgress: {},
          lastAccessedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          enrolled: true
        };
      }

      if (typeof totalLessons !== 'number' || totalLessons < 0) {
        throw new ValidationError('Total lessons must be a non-negative number');
      }

      const progressRef = this._getUserProgressRef(userId);
      const progressDoc = await getDoc(progressRef);

      let existingData: ProgressData = {};
      if (progressDoc.exists()) {
        existingData = progressDoc.data() as ProgressData;
      }

      if (!existingData[courseId]) {
        const courseProgress: CourseProgress = {
          overallProgress: 0,
          completedLessons: 0,
          totalLessons: totalLessons,
          totalInstructionSeconds: 0,
          lessonProgress: {},
          moduleProgress: {},
          lastAccessedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          enrolled: true
        };

        const mergedData = { ...existingData, [courseId]: courseProgress };
        await setDoc(progressRef, mergedData, { merge: true });

        this._setCache(userId, courseId, courseProgress);
        return courseProgress;
      } else {
        const currentProgress = existingData[courseId];
        const updatedProgress = {
          ...currentProgress,
          lastAccessedAt: serverTimestamp()
        };

        const completedCount = Object.values(currentProgress.lessonProgress || {}).filter(
          (lesson: any) => lesson.completed
        ).length;

        const expectedOverall = totalLessons > 0
          ? Math.round((completedCount / totalLessons) * 100)
          : (currentProgress.totalLessons > 0 ? Math.round((completedCount / currentProgress.totalLessons) * 100) : 0);

        let needsSync = false;

        // Self-heal totalLessons mismatch
        if (totalLessons > 0 && currentProgress.totalLessons !== totalLessons) {
          updatedProgress.totalLessons = totalLessons;
          needsSync = true;
        }

        // Self-heal completedLessons mismatch
        if (currentProgress.completedLessons !== completedCount) {
          updatedProgress.completedLessons = completedCount;
          needsSync = true;
        }

        // Self-heal overallProgress mismatch
        if (currentProgress.overallProgress !== expectedOverall) {
          updatedProgress.overallProgress = expectedOverall;
          needsSync = true;
        }

        if (needsSync) {
          updatedProgress.lastModified = serverTimestamp();
        }

        await updateDoc(progressRef, {
          [courseId]: updatedProgress
        });

        this._setCache(userId, courseId, updatedProgress);
        return updatedProgress;
      }
    } catch (error) {
      this.logError(error, { method: 'initializeProgress', userId, courseId });
      throw error;
    }
  }

  /**
   * Get user's progress for a specific course
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {boolean} bypassCache Whether to force a fresh fetch from Firestore
   * @returns {Promise<Object>} Course progress
   */
  async getProgress(userId: string, courseId: string, bypassCache = false): Promise<CourseProgress> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      const defaultProgress: CourseProgress = {
        overallProgress: 0,
        completedLessons: 0,
        totalLessons: 0,
        lessonProgress: {},
        moduleProgress: {},
        lastAccessedAt: null,
        createdAt: null,
        enrolled: false
      };

      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED Progress for E2E testing');
        return {
          ...defaultProgress,
          ...((window as any).MOCK_PROGRESS[courseId] || {}),
          enrolled: true
        };
      }

      if (!bypassCache) {
        const cached = this._getFromCache(userId, courseId);
        if (cached) {
          return { ...defaultProgress, ...cached };
        }
      }

      const progressRef = this._getUserProgressRef(userId);
      const progressDoc = await getDoc(progressRef);

      if (!progressDoc.exists()) {
        this._setCache(userId, courseId, defaultProgress);
        return defaultProgress;
      }

      const allProgress = progressDoc.data() as ProgressData;
      const courseProgress = allProgress[courseId];

      if (!courseProgress) {
        this._setCache(userId, courseId, defaultProgress);
        return defaultProgress;
      }

      const mergedProgress = { ...defaultProgress, ...courseProgress };
      this._setCache(userId, courseId, mergedProgress);
      return mergedProgress;
    } catch (error) {
      this.logError(error, { method: 'getProgress', userId, courseId });
      throw error;
    }
  }

  /**
   * Save or update user progress for a specific course
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {Object} progressData Full progress data to save
   * @returns {Promise<Object>} Saved progress
   * @warning This method will OVERWRITE the entire course object with the provided data.
   * Use updateProgress or updateLessonProgress for partial updates.
   */
  async saveProgress(userId: string, courseId: string, progressData: Partial<CourseProgress>): Promise<Partial<CourseProgress>> {
    try {
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED Save Progress for E2E testing');
        return progressData;
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!progressData || typeof progressData !== 'object') {
        throw new ValidationError('progressData must be a non-empty object');
      }

      const progressRef = this._getUserProgressRef(userId);
      const updateData = {
        [courseId]: progressData,
        lastModified: serverTimestamp()
      };

      await updateDoc(progressRef, updateData as any);
      this._invalidateCache(userId, courseId);
      return progressData;
    } catch (error) {
      this.logError(error, { method: 'saveProgress', userId, courseId, progressData });
      throw error;
    }
  }

  /**
   * Update specific fields in user progress
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {Object} updates Fields to update
   * @returns {Promise<Object>} Applied updates
   */
  async updateProgress(userId: string, courseId: string, updates: Record<string, any>): Promise<Record<string, any>> {
    try {
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED Update Progress for E2E testing');
        return updates;
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!updates || typeof updates !== 'object') {
        throw new ValidationError('updates must be a non-empty object');
      }

      const progressRef = this._getUserProgressRef(userId);
      const updateData: Record<string, any> = {};

      for (const [key, value] of Object.entries(updates)) {
        updateData[`${courseId}.${key}`] = value;
      }
      updateData['lastModified'] = serverTimestamp();

      await updateDoc(progressRef, updateData);
      this._invalidateCache(userId, courseId);
      return updates;
    } catch (error) {
      this.logError(error, { method: 'updateProgress', userId, courseId, updates });
      throw error;
    }
  }

  /**
   * Mark a single lesson as complete (without compliance)
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} lessonId Lesson ID
   * @returns {Promise<Object>} Update result
   */
  async markLessonComplete(userId: string, courseId: string, lessonId: string): Promise<any> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateLessonId(lessonId);

      const progressRef = this._getUserProgressRef(userId);
      const progressDoc = await getDoc(progressRef);
      const allProgress = progressDoc.exists() ? progressDoc.data() as ProgressData : {};
      const progress = allProgress[courseId] || ({} as CourseProgress);
      const lessonProgress = { ...(progress.lessonProgress || {}) };

      // Set this lesson as completed in our local calculation
      lessonProgress[lessonId] = {
        ...(lessonProgress[lessonId] || {}),
        completed: true,
        completedAt: new Date().toISOString()
      };

      const completedLessons = Object.keys(lessonProgress).filter(
        id => lessonProgress[id].completed
      ).length;
      
      const overallProgress = progress.totalLessons > 0
        ? Math.round((completedLessons / progress.totalLessons) * 100)
        : 0;

      const updateData = {
        [`${courseId}.lessonProgress.${lessonId}.completed`]: true,
        [`${courseId}.lessonProgress.${lessonId}.completedAt`]: serverTimestamp(),
        [`${courseId}.lessonProgress.${lessonId}.attempts`]: increment(1),
        [`${courseId}.completedLessons`]: completedLessons,
        [`${courseId}.overallProgress`]: overallProgress,
        [`${courseId}.lastModified`]: serverTimestamp()
      };

      await updateDoc(progressRef, updateData);
      this._invalidateCache(userId, courseId);
      return { lessonId, completed: true };
    } catch (error) {
      this.logError(error, { method: 'markLessonComplete', userId, courseId, lessonId });
      throw error;
    }
  }

  /**
   * Mark lesson complete WITH compliance tracking (atomic batch)
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} lessonId Lesson ID
   * @param {Object} complianceData Compliance data
   * @returns {Promise<Object>} Resulting progress metrics
   */
  async markLessonCompleteWithCompliance(userId: string, courseId: string, lessonId: string, complianceData: any): Promise<any> {
    try {
      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED markLessonCompleteWithCompliance for E2E testing');
        return {
          lessonId,
          completedLessons: 1,
          overallProgress: 50,
          sessionTime: complianceData.sessionTime
        };
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateLessonId(lessonId);

      if (!complianceData || typeof complianceData !== 'object') {
        throw new ValidationError('complianceData must be a non-empty object');
      }

      if (!complianceData.sessionId) {
        throw new ValidationError('complianceData.sessionId is required');
      }

      const progressRef = this._getUserProgressRef(userId);
      const progressDoc = await getDoc(progressRef);
      const allProgress = progressDoc.exists() ? progressDoc.data() as ProgressData : {};
      const progress = allProgress[courseId] || ({} as CourseProgress);
      const lessonProgress = { ...(progress.lessonProgress || {}) };

      const existingLessonProgress = lessonProgress[lessonId] || { attempts: 0 };

      const newAttempts = (existingLessonProgress.attempts || 0) + 1;

      const completedLessons = Object.keys(lessonProgress).filter(
        id => id === lessonId || lessonProgress[id].completed
      ).length;
      const overallProgress = progress.totalLessons > 0
        ? Math.round((completedLessons / progress.totalLessons) * 100)
        : 0;

      const batch = this.batch();

      batch.update(progressRef, {
        [`${courseId}.lessonProgress.${lessonId}.completed`]: true,
        [`${courseId}.lessonProgress.${lessonId}.completedAt`]: serverTimestamp(),
        [`${courseId}.lessonProgress.${lessonId}.attempts`]: increment(1),
        [`${courseId}.completedLessons`]: increment(1),
        [`${courseId}.overallProgress`]: overallProgress,
        [`${courseId}.lastModified`]: serverTimestamp()
      });

      const sessionRef = doc(db, 'users', userId, 'sessions', complianceData.sessionId);
      const completionEvent = {
        type: 'lesson_completion',
        lessonId,
        lessonTitle: complianceData.lessonTitle,
        moduleId: complianceData.moduleId,
        moduleTitle: complianceData.moduleTitle,
        sessionTime: complianceData.sessionTime,
        videoProgress: complianceData.videoProgress || null,
        completedAt: new Date().toISOString(),
        timestamp: new Date().toISOString()
      };
      batch.update(sessionRef, {
        completionEvents: arrayUnion(completionEvent)
      });

      await batch.commit();
      this._invalidateCache(userId, courseId);

      return {
        lessonId,
        completedLessons,
        overallProgress,
        sessionTime: complianceData.sessionTime
      };
    } catch (error) {
      this.logError(error, { method: 'markLessonCompleteWithCompliance', userId, courseId, lessonId });
      throw error;
    }
  }

  /**
   * Mark module as complete (without compliance)
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} moduleId Module ID
   * @returns {Promise<Object>} Update result
   */
  async markModuleComplete(userId: string, courseId: string, moduleId: string): Promise<any> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateModuleId(moduleId);

      const progressRef = this._getUserProgressRef(userId);
      const updateData = {
        [`${courseId}.moduleProgress.${moduleId}.completed`]: true,
        [`${courseId}.moduleProgress.${moduleId}.completedAt`]: serverTimestamp(),
        [`${courseId}.lastModified`]: serverTimestamp()
      };

      await updateDoc(progressRef, updateData);
      this._invalidateCache(userId, courseId);
      return { moduleId, completed: true };
    } catch (error) {
      this.logError(error, { method: 'markModuleComplete', userId, courseId, moduleId });
      throw error;
    }
  }

  /**
   * Mark module complete WITH compliance tracking (atomic batch)
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} moduleId Module ID
   * @param {Object} complianceData Compliance data
   * @returns {Promise<Object>} Result metrics
   */
  async markModuleCompleteWithCompliance(userId: string, courseId: string, moduleId: string, complianceData: any): Promise<any> {
    try {
      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED markModuleCompleteWithCompliance for E2E testing');
        return {
          moduleId,
          sessionTime: complianceData.sessionTime
        };
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateModuleId(moduleId);

      if (!complianceData || typeof complianceData !== 'object') {
        throw new ValidationError('complianceData must be a non-empty object');
      }

      if (!complianceData.sessionId) {
        throw new ValidationError('complianceData.sessionId is required');
      }

      const progressRef = this._getUserProgressRef(userId);
      const batch = this.batch();

      batch.update(progressRef, {
        [`${courseId}.moduleProgress.${moduleId}.completed`]: true,
        [`${courseId}.moduleProgress.${moduleId}.completedAt`]: serverTimestamp(),
        [`${courseId}.lastModified`]: serverTimestamp()
      });

      const sessionRef = doc(db, 'users', userId, 'sessions', complianceData.sessionId);
      const completionEvent = {
        type: 'module_completion',
        moduleId,
        moduleTitle: complianceData.moduleTitle,
        sessionTime: complianceData.sessionTime,
        completedAt: new Date().toISOString(),
        timestamp: new Date().toISOString()
      };
      batch.update(sessionRef, {
        completionEvents: arrayUnion(completionEvent)
      });

      await batch.commit();
      this._invalidateCache(userId, courseId);

      return {
        moduleId,
        sessionTime: complianceData.sessionTime
      };
    } catch (error) {
      this.logError(error, { method: 'markModuleCompleteWithCompliance', userId, courseId, moduleId });
      throw error;
    }
  }

  /**
   * Update progress for a lesson
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} lessonId Lesson ID
   * @param {Object} progressData Progress data to update
   * @returns {Promise<Object>} Applied updates
   */
  async updateLessonProgress(userId: string, courseId: string, lessonId: string, progressData: Partial<LessonProgress>): Promise<Partial<LessonProgress>> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateLessonId(lessonId);

      if (typeof window !== 'undefined' && (window as any).MOCK_PROGRESS) {
        this.log('Returning MOCKED Update Lesson Progress for E2E testing');
        return progressData;
      }

      if (!progressData || typeof progressData !== 'object') {
        throw new ValidationError('progressData must be a non-empty object');
      }

      const progressRef = this._getUserProgressRef(userId);
      const updateData: Record<string, any> = {};

      for (const [key, value] of Object.entries(progressData)) {
        updateData[`${courseId}.lessonProgress.${lessonId}.${key}`] = value;
      }
      updateData[`${courseId}.lastModified`] = serverTimestamp();

      await updateDoc(progressRef, updateData);
      this._invalidateCache(userId, courseId);
      return progressData;
    } catch (error) {
      this.logError(error, { method: 'updateLessonProgress', userId, courseId, lessonId });
      throw error;
    }
  }

  /**
   * Update supplemental video progress for a specific URL within a lesson
   */
  async updateLessonSupplementalVideoProgress(
    userId: string,
    courseId: string,
    lessonId: string,
    videoUrl: string,
    progress: { currentTime: number; duration: number; percentWatched: number }
  ): Promise<void> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validateLessonId(lessonId);

      // Sanitize videoUrl for Firestore key (replace non-alphanumeric characters with underscores)
      const sanitizedUrl = videoUrl.replace(/[^a-zA-Z0-9]/g, '_');

      const progressRef = this._getUserProgressRef(userId);
      const updateData: Record<string, any> = {
        [`${courseId}.lessonProgress.${lessonId}.supplementalVideoProgress.${sanitizedUrl}`]: progress,
        [`${courseId}.lastModified`]: serverTimestamp()
      };

      await updateDoc(progressRef, updateData);
      this._invalidateCache(userId, courseId);
    } catch (error) {
      this.logError(error, { method: 'updateLessonSupplementalVideoProgress', userId, courseId, lessonId, videoUrl });
      throw error;
    }
  }

  /**
   * Increment the cumulative instruction time for a course
   * @param {string} userId - Student ID
   * @param {string} courseId - Course ID
   * @param {number} secondsToAdd - Seconds to add to total
   */
  async incrementCumulativeTime(userId: string, courseId: string, secondsToAdd: number): Promise<void> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      if (typeof secondsToAdd !== 'number' || secondsToAdd <= 0) {
        return;
      }

      const progressRef = this._getUserProgressRef(userId);
      await updateDoc(progressRef, {
        [`${courseId}.totalInstructionSeconds`]: increment(secondsToAdd),
        [`${courseId}.lastAccessedAt`]: serverTimestamp(),
        'lastModified': serverTimestamp()
      });
      this._invalidateCache(userId, courseId);
    } catch (error) {
      this.logError(error, { method: 'incrementCumulativeTime', userId, courseId, secondsToAdd });
      throw error;
    }
  }

  /**
   * Get user stats across all courses
   * @param {string} userId User ID
   * @returns {Promise<Object>} Aggregated stats
   */
  async getUserStats(userId: string): Promise<UserStats> {
    try {
      this.validate.validateUserId(userId);

      const progressRef = this._getUserProgressRef(userId);
      const progressDoc = await getDoc(progressRef);

      if (!progressDoc.exists()) {
        return {
          totalCoursesEnrolled: 0,
          totalLessonsCompleted: 0,
          averageProgress: 0
        };
      }

      const data = progressDoc.data() as ProgressData;
      let totalCoursesEnrolled = 0;
      let totalLessonsCompleted = 0;
      let totalProgress = 0;

      for (const courseId in data) {
        const courseData = data[courseId] as CourseProgress;
        if (courseData && courseData.enrolled) {
          totalCoursesEnrolled++;
          totalLessonsCompleted += courseData.completedLessons || 0;
          totalProgress += courseData.overallProgress || 0;
        }
      }

      const averageProgress = totalCoursesEnrolled > 0
        ? Math.round(totalProgress / totalCoursesEnrolled)
        : 0;

      return {
        totalCoursesEnrolled,
        totalLessonsCompleted,
        averageProgress
      };
    } catch (error) {
      this.logError(error, { method: 'getUserStats', userId });
      throw error;
    }
  }
}

const progressServices = new ProgressService();
export default progressServices;

// Maintain backward compatibility for named exports
export const initializeProgress = (uId: string, cId: string, total?: number) => progressServices.initializeProgress(uId, cId, total);
export const getProgress = (uId: string, cId: string, bypass?: boolean) => progressServices.getProgress(uId, cId, bypass);
export const saveProgress = (uId: string, cId: string, data: any) => progressServices.saveProgress(uId, cId, data);
export const updateProgress = (uId: string, cId: string, updates: any) => progressServices.updateProgress(uId, cId, updates);
export const markLessonComplete = (uId: string, cId: string, lId: string) => progressServices.markLessonComplete(uId, cId, lId);
export const markLessonCompleteWithCompliance = (uId: string, cId: string, lId: string, data: any) => progressServices.markLessonCompleteWithCompliance(uId, cId, lId, data);
export const markModuleComplete = (uId: string, cId: string, mId: string) => progressServices.markModuleComplete(uId, cId, mId);
export const markModuleCompleteWithCompliance = (uId: string, cId: string, mId: string, data: any) => progressServices.markModuleCompleteWithCompliance(uId, cId, mId, data);
export const updateLessonProgress = (uId: string, cId: string, lId: string, data: any) => progressServices.updateLessonProgress(uId, cId, lId, data);
export const updateLessonSupplementalVideoProgress = (uId: string, cId: string, lId: string, url: string, data: any) =>
  progressServices.updateLessonSupplementalVideoProgress(uId, cId, lId, url, data);
export const incrementCumulativeTime = (uId: string, cId: string, sec: number) => progressServices.incrementCumulativeTime(uId, cId, sec);
export const getUserStats = (uId: string) => progressServices.getUserStats(uId);

/**
 * Clear the progress cache.
 * Primarily used for testing to ensure isolation.
 */
export const _clearProgressCache = () => progressServices.clearCache();

export async function fetchIpAddress(): Promise<string> {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip;
  } catch {
    return 'unknown';
  }
}
